import * as React from "react";

import { SocialLink } from "@/api/types";
import { NETWORK_LABELS, SocialIcon } from "@/components/SocialIcon";
import { PRIVACY_POLICY_URL } from "@/lib/legalLinks";
import { cn } from "@/lib/utils";

import { EmbedOptions, useReportHeight } from "./embedMode";

/**
 * The shell every anonymous invitee page wears: the card, and the banner header above it.
 *
 * <p>Both were a byte-identical private copy in {@code PublicBookingPage} and
 * {@code PublicRollingLinkPage}. Adding a third invitee page (polls) was the moment to lift them rather
 * than make a third — three copies of a header is how three invitee pages start drifting apart,
 * and an invitee has no idea they are looking at different features.</p>
 *
 * <p>A pure move: no visual change to either existing page.</p>
 */

/**
 * The page shell an embeddable booking page wears: the tinted full-height background, the page
 * padding, and the centred column its cards stack in.
 *
 * <p>Embed mode changes exactly this layer and nothing below it. A page framed inside somebody
 * else's site must not claim the full viewport height, must not paint its own grey background
 * across their layout, and needs less padding because their page already provides it.</p>
 *
 * <p><b>Only the rolling link uses this.</b> The other five invitee pages still hand-roll the same
 * two divs, and that duplication is left alone on purpose: a rolling link is the only one we
 * embed, and lifting the other pages onto a shell they do not need would put embed parameters
 * within reach of pages the feature does not cover. The ad-hoc and poll pages adopt it when they
 * get an embed of their own, not before.</p>
 *
 * <p>Opened directly, this renders exactly what the rolling page rendered before it existed.</p>
 */
export function PublicPageShell({
  embed,
  width,
  children,
}: {
  embed: EmbedOptions;
  /** Tailwind max-width for the column. The pages widen once the slot picker is on screen. */
  width: string;
  children: React.ReactNode;
}) {
  // Measured on the OUTER element, so the reported height includes the page padding. Measuring
  // the inner column would report a height the host then sizes to exactly, clipping the padding
  // and leaving the bottom card touching the edge of the frame.
  const outer = React.useRef<HTMLDivElement>(null);
  useReportHeight(embed, outer);

  return (
    <div
      ref={outer}
      className={cn(
        "inv-theme px-4",
        // Embedded: no background of our own, so the host's page shows through and the widget
        // reads as part of their site rather than as a grey slab dropped into it. And no
        // min-h-screen, which inside an iframe is precisely what produces the scrollbar inside
        // a scrollbar that fixed-height embeds are notorious for.
        embed.embedded ? "py-4" : "min-h-screen bg-muted/30 py-10",
      )}
    >
      <div className={cn("mx-auto flex flex-col gap-5", width)}>{children}</div>
    </div>
  );
}

/**
 * The legal footer every anonymous invitee page wears.
 *
 * <p>It existed as a byte-identical copy on the ad-hoc and rolling booking pages and was
 * simply absent from the other four invitee pages, so somebody answering a poll, or managing
 * a booking from their confirmation email, was offered no privacy notice at all. That is a
 * transparency gap rather than a cosmetic one: those pages collect a name and an email
 * address from a person who has no account and never visited the marketing site, and the
 * booking pages are the ONLY place that person is ever told what happens to it.</p>
 *
 * <p><b>Privacy and Cookies only. Deliberately no link to /terms.</b> Those are the CUSTOMER
 * terms: they bind the host who holds the account, not the invitee reading this page. Their
 * clauses about billing, plan limits and handles describe a service the invitee never signed
 * up for, so pointing them here would assert an agreement that does not exist. Invitees need
 * their own short terms, and when that document ships this is the one place to add it.</p>
 */
export function PublicFooter({ className }: { className?: string }) {
  return (
    // The booking pages stack in a `flex-col gap-5`, so they need no margin of their own.
    // The manage pages do not, hence the opt-in spacing rather than a baked-in margin that
    // would double up on the pages that already have it.
    <p className={cn("text-center text-xs text-muted-foreground", className)}>
      {/* The brand mark carries the blue it wore in the lockup that used to sit above the card.
          That lockup is gone (it said the same thing twice on one screen), so this is now the
          only place an invitee sees whose product they are on, and a grey word in a legal line
          is not a signature. The rest of the line stays muted: it is a footer, not a header. */}
      Powered by <span className="font-semibold text-brand-blue">Scheda</span> · Delgentic ·{" "}
      <a
        href={PRIVACY_POLICY_URL}
        target="_blank"
        rel="noreferrer"
        className="underline underline-offset-2 hover:text-foreground"
      >
        Privacy
      </a>{" "}
      ·{" "}
      <a href="/cookies" className="underline underline-offset-2 hover:text-foreground">
        Cookies
      </a>
    </p>
  );
}

export function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("overflow-hidden rounded-2xl border border-border bg-card shadow-sm", className)}>
      {children}
    </div>
  );
}

/**
 * Banner header, echoing the Microsoft Bookings profile card, on the landing palette.
 *
 * <p>Shared by all three invitee pages, so the host's picture appears identically on an ad-hoc link,
 * a rolling link and a poll. An invitee has no idea they are looking at different features, and this
 * is the one place that keeps them from looking like different products.</p>
 *
 * <p>{@code avatarUrl} is null unless the host's plan includes CUSTOM_BRANDING, and null falls back
 * to their initials rather than to an empty circle — a page with a grey void where a face should be
 * looks broken, whereas initials look deliberate.</p>
 *
 * <p><b>96px is not arbitrary.</b> It is the size the cropper is calibrated to: the circle a host
 * frames their photo in is this circle, and the stored image is 512px square, so it stays crisp at
 * 3x. The image is a square with a CSS mask, not a pre-cut circle, so nothing was destroyed on
 * upload and the corners are still there if this ever becomes a rounded square.</p>
 */
export function HeaderCard({
  name,
  subtitle,
  avatarUrl,
  bannerUrl,
  socialLinks,
}: {
  name: string;
  subtitle: string;
  avatarUrl?: string | null;
  bannerUrl?: string | null;
  socialLinks?: SocialLink[] | null;
}) {
  // A banner that fails to load falls back to the gradient, which is the same thing a host with no
  // banner gets. Tracked rather than assumed: the underlay below depends on there being a real
  // image, so "we asked for one" is not good enough a fact to build on.
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => setBroken(false), [bannerUrl]);
  const usable = Boolean(bannerUrl) && !broken;

  // NOTE: this component knows nothing about embedding, deliberately. It is shared by all six
  // invitee pages, so reading hide_header here would apply it to the ad-hoc and poll pages too —
  // pages the embed does not cover, and which would then honour ?embed=1&hide_header=1 on a plain
  // link. The rolling page decides for itself at its own call sites.
  return (
    <Card>
      {/*
        3:1. The underlay is the CARD's own colour when there is a usable banner, and the gradient
        otherwise.

        That swap is what puts a fitted banner on white. A host whose logo is not 3:1 frames it with
        "Fit whole image", and the stored file keeps its alpha where the logo does not reach — so
        whatever sits behind it shows through. Painting the gradient there would ring a wordmark in
        blue; painting white would be wrong after dark. The card's background is right in both
        themes, and is what the rest of the page is already made of.

        The gradient stays for the no-banner and failed-to-load cases, so the card still looks
        finished rather than showing an empty slab.
      */}
      <div
        className="relative aspect-[3/1] w-full bg-card"
        style={usable ? undefined : { background: BANNER_GRADIENT }}
      >
        {bannerUrl && (
          // object-cover crops nothing unexpected: the stored file is already 3:1, because that is
          // the frame the host positioned it in.
          <img
            src={bannerUrl}
            alt=""
            onError={() => setBroken(true)}
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
      </div>
      {/*
        `relative z-10` is load-bearing, not decoration. The banner above is positioned (it has to
        be, to hold the image), and a positioned element paints ABOVE a static sibling whatever the
        DOM order — so without this the banner covers the top half of the avatar and the circle looks
        sliced off. It was fine until the banner became positioned.
      */}
      <div className="relative z-10 px-6 pb-5">
        {/* Pulled up into the banner so the circle straddles the edge, and the title sits beside the
            avatar rather than under it. */}
        <div className="-mt-12 flex flex-wrap items-end gap-x-4 gap-y-3">
          <Avatar name={name} url={avatarUrl} />
          <div className="min-w-0 flex-1 pb-1">
            <h1 className="truncate text-xl font-bold tracking-tight text-foreground">{name}</h1>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          </div>
          <SocialRow links={socialLinks} />
        </div>
      </div>
    </Card>
  );
}

/**
 * The default header, for hosts with no banner of their own.
 *
 * <p>Exported so the profile page can preview the same thing a host would get before they upload —
 * two copies of this would drift, and the preview would then be quietly wrong.</p>
 */
export const BANNER_GRADIENT =
  "radial-gradient(120% 140% at 15% -20%, rgba(255,255,255,.30), transparent 55%), " +
  "repeating-linear-gradient(115deg, rgba(255,255,255,.10) 0 22px, rgba(255,255,255,0) 22px 46px), " +
  "linear-gradient(120deg, #1e5f83, #2779a7 45%, #49c5b6)";

/**
 * The host's social profiles, as outbound links.
 *
 * <p>Free at every tier, unlike the pictures — a host's links are theirs, and hiding them behind a
 * plan would make an unbranded page look abandoned rather than plain.</p>
 *
 * <p>{@code rel="noopener noreferrer"} and {@code target="_blank"}: these are third-party
 * destinations opened from a page an anonymous invitee is mid-booking on, and losing their place
 * would cost a booking. The URLs were canonicalised and host-checked server-side, so the icon and
 * the destination cannot disagree.</p>
 *
 * <h3>One outline around the set, not one around each</h3>
 *
 * <p>The marks are deliberately our own monochrome outlines rather than the real logos, which
 * leaves them with no colour of their own to be recognised by. Drawn in {@code muted-foreground}
 * they read as a row of greyed-out, disabled glyphs — the failure §27 of the design principles
 * names outright, and doubly wrong here because these are links (§26: small accents are
 * brand-blue).</p>
 *
 * <p>So they are brand-blue, and the set is enclosed by a single thin palette line (§33). A ring
 * per icon would make four buttons out of one idea, and would put a border around each mark, which
 * is closer to badging a logo than we want to be.</p>
 *
 * <p><b>The line follows the icons, not a box round them.</b> It is a capsule — flat top and bottom,
 * a round cap at each end — that bends in around each icon where two of them meet.</p>
 *
 * <p>Two earlier tries bracket this one. Tracing the union of a circle per icon bent nicely around
 * each but was all curve and no capsule. Notching the flat edge kept the capsule but the notch was
 * <em>concave</em> — an arc curving the opposite way to everything else on a card built from round
 * corners and round caps, so it read as a bite taken out rather than as a shape hugging something.
 * </p>
 *
 * <p>So the corners here curve the same way a rounded corner does: away from the flat, around the
 * icon. Each icon keeps a flat run over it, and at the boundary the edge turns down convexly, meets
 * its neighbour's, and turns back up. See {@link capsulePath}.</p>
 */
function SocialRow({ links }: { links?: SocialLink[] | null }) {
  if (!links || links.length === 0) return null;
  return (
    <div className="pb-1">
      <ul className="relative flex list-none items-center">
        {/* Behind the icons and deaf to the mouse: it is the shape of the set, not a control. */}
        <CapsuleOutline count={links.length} />
        {links.map((link) => (
          // No margins and no nudging: equal slots, laid end to end, are what put each icon in the
          // middle of its own stretch of capsule.
          <li key={link.network}>
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              title={`${NETWORK_LABELS[link.network]}: ${link.label}`}
              aria-label={`${NETWORK_LABELS[link.network]} profile`}
              // Fills its slot. The hover shade is a circle, and the outline's corners now curve
              // the same way it does — a concave notch was a shape arguing with the fill inside it.
              style={{ width: SLOT, height: SLOT }}
              className="relative grid place-items-center rounded-full text-brand-blue transition hover:bg-brand-blue/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
            >
              <SocialIcon network={link.network} className="h-4 w-4" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Geometry of the outline, shared by the path and the cells it is drawn around.
 *
 * <p>One slot per icon, all equal, so the icons need no positioning of their own: each sits in the
 * middle of its slot, and the pinches sit on the slot boundaries. Centred between the pinches falls
 * out of that rather than being arranged.</p>
 */
const round = (n: number) => Math.round(n * 100) / 100;

const SLOT = 36; // width of one icon's slot, and the height of the capsule
const CAP = SLOT / 2; // so the ends are semicircles
const FLAT_HALF = 10; // half the flat run kept over each icon, out of SLOT / 2
const PINCH = 4; // how far the edge comes in at a boundary, out of CAP

/** Horizontal room each corner has to turn in: what is left of the half-slot after the flat. */
const TURN = SLOT / 2 - FLAT_HALF;

/**
 * Radius that turns through {@link TURN} horizontally while dropping {@link PINCH} vertically.
 *
 * <p>From r - sqrt(r^2 - TURN^2) = PINCH. Deriving it rather than picking one is what keeps the
 * corner tangent to the flat run at one end and exactly on the pinch at the other, so the join
 * never shows as a kink.</p>
 */
const CORNER = round((TURN ** 2 + PINCH ** 2) / (2 * PINCH));

/**
 * A capsule of {@code count} slots, bending in at every interior slot boundary.
 *
 * <p>Walked clockwise from where the left cap meets the flat. Each boundary is two arcs, not one:
 * the edge turns down away from the flat, then its neighbour turns back up, and both curve the same
 * way a rounded corner does. That is why their sweeps differ — mirrored corners turn opposite ways
 * — and why a single arc across the gap would have been the concave bite this replaced.</p>
 *
 * <p>With one icon there are no boundaries and it is simply a circle.</p>
 */
function capsulePath(count: number): string {
  const w = SLOT * count;
  const arc = (sweep: 0 | 1, x: number, y: number) =>
    `A ${CORNER} ${CORNER} 0 0 ${sweep} ${round(x)} ${round(y)}`;

  const d = [`M ${CAP} 0`];
  for (let i = 1; i < count; i++) {
    const x = SLOT * i;
    d.push(`L ${x - TURN} 0`, arc(1, x, PINCH), arc(0, x + TURN, 0));
  }
  d.push(`L ${w - CAP} 0`, `A ${CAP} ${CAP} 0 0 1 ${w - CAP} ${SLOT}`);
  for (let i = count - 1; i > 0; i--) {
    const x = SLOT * i;
    d.push(`L ${x + TURN} ${SLOT}`, arc(1, x, SLOT - PINCH), arc(0, x - TURN, SLOT));
  }
  d.push(`L ${CAP} ${SLOT}`, `A ${CAP} ${CAP} 0 0 1 ${CAP} 0`);
  return `${d.join(" ")} Z`;
}

function CapsuleOutline({ count }: { count: number }) {
  const width = SLOT * count;
  // One pixel of margin all round, or the stroke is clipped by its own viewBox.
  return (
    <svg
      aria-hidden="true"
      width={width + 2}
      height={SLOT + 2}
      viewBox={`-1 -1 ${width + 2} ${SLOT + 2}`}
      fill="none"
      stroke="currentColor"
      strokeWidth={1}
      className="pointer-events-none absolute -left-px -top-px text-brand-blue/50"
    >
      <path d={capsulePath(count)} />
    </svg>
  );
}

/** The host's picture, or their initials. Ring in the card colour so it reads as lifted off the banner. */
function Avatar({ name, url }: { name: string; url?: string | null }) {
  const shell =
    "h-24 w-24 shrink-0 rounded-full ring-4 ring-card shadow-sm overflow-hidden bg-card";
  if (url) {
    return (
      <div className={shell}>
        {/* object-cover cannot crop anything unexpectedly: the stored file is already square,
            because that is exactly what the host framed. */}
        <img src={url} alt="" className="h-full w-full object-cover" />
      </div>
    );
  }
  return (
    <div
      className={cn(shell, "grid place-items-center text-2xl font-bold text-white")}
      style={{ background: "linear-gradient(135deg, #2779a7, #49c5b6)" }}
      aria-hidden="true"
    >
      {initials(name)}
    </div>
  );
}

/** Up to two letters from the display name. Falls back to a dot rather than rendering nothing. */
function initials(name: string): string {
  const parts = name.replace(/^@/, "").split(/[\s._-]+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p[0]).join("");
  return letters.toUpperCase() || "·";
}
